// Phase 15 Plan 06 Task 3 — Casual matchmaking enqueue modal.
//
// On open → POST /api/matchmaking/enqueue { mode: 'casual', language }
//        → server returns a ticket
//        → 2s setInterval poll on /api/matchmaking/status?ticket=...
//   - status === 'matched' → close modal + navigate('/match/{matchId}')
//   - status === 'expired' → swap body to a "bot-offer" CTA so the user
//     can pivot to a bot match instead of waiting longer (DEC-009-friendly).
//     The OpenAPI schema's enum is `'waiting' | 'matched' | 'expired'`
//     (the plan's `'bot_offer' | 'cancelled'` wording predates the
//     schema; deviation Rule 1 — adapt to real schema by mapping
//     `'expired'` → bot-offer surface).
//   - status === 'waiting' → continue polling
// On modal close (Escape, scrim) → DELETE /api/matchmaking/{ticket}.
//
// Cancel polling immediately on any state transition away from waiting
// or on modal close. The 30s lobby polling continues independently.

import { useStore } from '@nanostores/react'
import { type JSX, useCallback, useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { ApiError, apiFetch } from '../../net/client'
import { createIdempotencyKey } from '../../net/idempotency'
import { useRouter } from '../../router/router'
import { $locale } from '../../stores/locale'
import { Btn } from '../components/Btn'
import { Sheet } from '../components/Sheet'

type MatchmakingState =
  | 'enqueuing'
  | 'waiting'
  | 'matched'
  | 'bot_offer'
  | 'error'

interface EnqueueResponse {
  readonly ticket: string
  readonly lobbyWebsocketUrl?: string
  readonly pollUrl?: string
}

interface StatusResponse {
  readonly state: 'waiting' | 'matched' | 'expired'
  readonly matchId?: string
  readonly websocketUrl?: string
}

interface MatchCreateResponse {
  readonly matchId: string
}

export interface CasualEnqueueModalProps {
  readonly open: boolean
  readonly onClose: () => void
}

const POLL_INTERVAL_MS = 2000

export function CasualEnqueueModal({
  open,
  onClose,
}: CasualEnqueueModalProps): JSX.Element | null {
  const router = useRouter()
  const locale = useStore($locale)
  const [state, setState] = useState<MatchmakingState>('enqueuing')
  const ticketRef = useRef<string | null>(null)
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const mountedRef = useRef<boolean>(false)

  const clearPollTimer = useCallback((): void => {
    if (pollTimerRef.current !== null) {
      clearInterval(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }, [])

  const pollStatus = useCallback(async (): Promise<void> => {
    const ticket = ticketRef.current
    if (ticket === null) return
    try {
      const status = await apiFetch<StatusResponse>(
        `/api/matchmaking/status?ticket=${encodeURIComponent(ticket)}`,
        'GET',
      )
      if (status.state === 'matched' && status.matchId !== undefined) {
        clearPollTimer()
        if (mountedRef.current) {
          setState('matched')
          // Successful match — clear the ticket so close-cleanup doesn't
          // try to DELETE a consumed ticket.
          ticketRef.current = null
          onClose()
          router.navigate(`/match/${status.matchId}`)
        }
        return
      }
      if (status.state === 'expired') {
        clearPollTimer()
        if (mountedRef.current) {
          setState('bot_offer')
        }
        return
      }
      // waiting → keep polling.
    } catch {
      clearPollTimer()
      if (mountedRef.current) setState('error')
    }
  }, [clearPollTimer, onClose, router])

  const releaseTicket = useCallback((): void => {
    const ticket = ticketRef.current
    if (ticket === null) return
    ticketRef.current = null
    // Fire-and-forget — DELETE is idempotent server-side.
    void apiFetch(
      `/api/matchmaking/${encodeURIComponent(ticket)}`,
      'DELETE',
    ).catch(() => undefined)
  }, [])

  const enqueue = useCallback(async (): Promise<void> => {
    setState('enqueuing')
    try {
      const result = await apiFetch<EnqueueResponse>(
        '/api/matchmaking/enqueue',
        'POST',
        {
          body: { mode: 'casual', language: locale },
          idempotencyKey: createIdempotencyKey(),
        },
      )
      ticketRef.current = result.ticket
      if (!mountedRef.current) {
        // Component unmounted before enqueue resolved — release the ticket.
        releaseTicket()
        return
      }
      setState('waiting')
      // Start the 2s status poll.
      pollTimerRef.current = setInterval(() => {
        void pollStatus()
      }, POLL_INTERVAL_MS)
      // Fire one immediate poll so fast-match cases resolve quickly.
      void pollStatus()
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        // 409 = active-match cap. Surface a cap-reached toast and close.
        window.dispatchEvent(
          new CustomEvent('runegambit:toast', {
            detail: { id: 'social.error.cap_reached' },
          }),
        )
        onClose()
        return
      }
      if (mountedRef.current) setState('error')
    }
  }, [pollStatus, releaseTicket, onClose, locale])

  // Lifecycle: enqueue on open; teardown on close.
  useEffect(() => {
    if (!open) return
    mountedRef.current = true
    setState('enqueuing')
    void enqueue()
    return () => {
      mountedRef.current = false
      clearPollTimer()
      releaseTicket()
    }
  }, [open, enqueue, clearPollTimer, releaseTicket])

  const handleBotOfferConfirm = useCallback(async (): Promise<void> => {
    try {
      const result = await apiFetch<MatchCreateResponse>(
        '/api/match/create',
        'POST',
        {
          body: { mode: 'bot', language: locale },
          idempotencyKey: createIdempotencyKey(),
        },
      )
      onClose()
      router.navigate(`/match/${result.matchId}`)
    } catch {
      setState('error')
    }
  }, [onClose, router, locale])

  if (!open) return null

  return (
    // Bottom-sheet flavor (UI-SPEC §5): scrim + grab handle + focus trap +
    // scrim-tap/Escape dismissal all live in <Sheet>/ModalScrim.
    <Sheet
      onClose={onClose}
      testId="casual-enqueue-modal"
      labelledBy="casual-enqueue-modal-title"
    >
      <div
        style={{
          height: '48px',
          backgroundColor: 'var(--color-surface-deep)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          borderBottom: '3px solid var(--color-surface-stroke)',
        }}
      >
        <h2
          id="casual-enqueue-modal-title"
          style={{
            margin: 0,
            fontFamily: 'Fredoka, sans-serif',
            fontWeight: 700,
            fontSize: '18px',
            color: 'var(--color-text-primary)',
          }}
        >
          <FormattedMessage
            id="social.lobby.casual_modal.title"
            defaultMessage="Finding a match"
          />
        </h2>
      </div>
      <div
        style={{
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          color: 'var(--color-text-primary)',
          fontFamily: 'Manrope, system-ui, sans-serif',
        }}
      >
        {state === 'enqueuing' && (
          <p
            data-testid="casual-state-enqueuing"
            aria-live="polite"
            style={{ margin: 0 }}
          >
            <FormattedMessage
              id="social.lobby.casual_modal.enqueuing"
              defaultMessage="Joining the queue…"
            />
          </p>
        )}
        {state === 'waiting' && (
          <p
            data-testid="casual-state-waiting"
            aria-live="polite"
            style={{ margin: 0 }}
          >
            <FormattedMessage
              id="social.lobby.casual_modal.waiting"
              defaultMessage="Finding opponent…"
            />
          </p>
        )}
        {state === 'matched' && (
          <p
            data-testid="casual-state-matched"
            aria-live="polite"
            style={{ margin: 0 }}
          >
            <FormattedMessage
              id="social.lobby.casual_modal.matched"
              defaultMessage="Match found!"
            />
          </p>
        )}
        {state === 'bot_offer' && (
          <>
            <p
              data-testid="casual-state-bot-offer"
              aria-live="polite"
              style={{ margin: 0 }}
            >
              <FormattedMessage
                id="social.lobby.casual_modal.bot_offer"
                defaultMessage="Nobody's around. Play vs a bot now?"
              />
            </p>
            <Btn
              kind="primary"
              size="lg"
              block
              testId="casual-bot-offer-confirm"
              onClick={() => {
                void handleBotOfferConfirm()
              }}
            >
              <FormattedMessage
                id="social.lobby.casual_modal.bot_offer_cta"
                defaultMessage="Play vs a bot"
              />
            </Btn>
          </>
        )}
        {state === 'error' && (
          <p
            role="alert"
            data-testid="casual-state-error"
            style={{ margin: 0, color: 'var(--color-cta-destructive)' }}
          >
            <FormattedMessage
              id="social.error.matchmaking_failed"
              defaultMessage="Matchmaking hiccupped. Try again."
            />
          </p>
        )}
      </div>
    </Sheet>
  )
}
